import { Component, Inject } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ChangeEvent } from '@ckeditor/ckeditor5-angular';
import ClassicEditorBase from '@ckeditor/ckeditor5-build-classic';
import { FileSystemFileEntry, NgxFileDropEntry } from 'ngx-file-drop';
import { TranslateService } from '@ngx-translate/core';
import { EmailService } from 'src/app/core/services/email/email.service';
import { FileService } from 'src/app/core/services/file/file.service';
import { SnackBar } from 'src/app/core/utility/snackBar';

const ALLOWED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png'];
const ALLOWED_MIME_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];

@Component({
	selector: 'app-dialog-email',
	templateUrl: './dialog-email.component.html',
	styleUrls: ['./dialog-email.component.scss'],
})
export class DialogEmailComponent {
	mailDetailForm: UntypedFormGroup;
	mailMessage;
	upEnabled: boolean;
	langSelected = 'nl';
	selectedIndex = 0;
	readOnly = true;

	bodyEditor = ClassicEditorBase;
	editorData: string;
	config = {
		toolbar: [
			'Heading',
			'|',
			'Bold',
			'Italic',
			'Link',
			'bulletedList',
			'numberedList',
			'|',
			'indent',
			'outdent',
			'|',
			// 'imageUpload',
			'blockQuote',
			'insertTable',
			'undo',
			'redo',
		],
	};
	mailAttachments: {
		filename: string;
		reference: string;
		progress: number;
		size: number;
		valid: boolean;
	}[] = [];
	maxFileSize = 1024 * 1024 * 10;
	maxAttachmentSize = 1024 * 1024 * 10;

	constructor(
		public dialogRef: MatDialogRef<DialogEmailComponent>,
		private formBuilder: UntypedFormBuilder,
		private fileService: FileService,
		private emailService: EmailService,
		private snackBar: SnackBar,
		private translate: TranslateService,
		@Inject(MAT_DIALOG_DATA) public data: any
	) {
		// handle incoming this.data here
		if (this.data && this.data.emails) {
			this.mailMessage = this.data.emails[0];
			this.readOnly = this.data?.readOnly;
			this.data.emails[0].attachments?.forEach((attachment) => {
				const mailAttachment = {
					filename: attachment.filename,
					reference: attachment.reference,
					progress: 100,
					size: 0,
					valid: true,
				};
				this.mailAttachments.push(mailAttachment);
				if (attachment.reference) {
					this.fileService.fileExists(attachment.reference).then((exists) => {
						mailAttachment.valid = exists;
					});
				}
			});
		}

		this.mailDetailForm = this.formBuilder.group({
			flightId: null,
			locationId: null,
			bookingId: null,
			crewId: null,
			from: null,
			replyTo: null,
			to: null,
			subject: null,
			html: null,
			text: null,
		});

		this.updateForm();
	}

	selectMail(i: number) {
		this.data.emails[this.selectedIndex] = this.mailDetailForm.value;
		this.selectedIndex = i;
		this.mailMessage = this.data.emails[i];
		this.updateForm();
	}

	updateForm() {
		this.mailDetailForm.patchValue(this.mailMessage);
		this.editorData = this.mailDetailForm.controls['html'].value;
	}

	cancel() {
		this.dialogRef.close({ status: 'cancel' });
	}

	send() {
		const mail = this.mailDetailForm.value;
		mail.html = this.convertText2Html(mail.html);

		// adding attachments — only include those that uploaded successfully
		mail.attachments = this.mailAttachments
			.filter((mailAttachment) => mailAttachment.valid && mailAttachment.reference)
			.map((mailAttachment) => {
				return {
					filename: mailAttachment.filename,
					reference: mailAttachment.reference,
				};
			});

		this.sendGridMail(mail);

		// remove from array
		this.data.emails.splice(this.selectedIndex, 1);

		if (this.data.emails.length === 0) {
			this.cancel();
		} else {
			// reset form
			this.selectedIndex = 0;
			this.mailMessage = this.data.emails[0];
			this.updateForm();
		}
	}

	sendGridMail(email) {
		this.emailService.addEmailOut(email).subscribe();
	}

	convertText2Html(text: string): string {
		if (text === '') return '';
		while (text.includes('\n')) {
			text = text.replace('\n', '<br>');
		}
		if (text.substring(0, 3) === '<p>') return text;
		return '<p>' + text + '</p>';
	}

	onChangeBody({ editor }: ChangeEvent) {
		this.mailDetailForm.controls['html'].patchValue(this.editorData);
	}

	onNativeFileSelected(event: Event) {
		const input = event.target as HTMLInputElement;
		if (!input.files?.length) return;
		Array.from(input.files).forEach((file) => this.processFile(file));
		input.value = '';
	}

	dropped(attachments: NgxFileDropEntry[]) {
		attachments.forEach((attachment) =>
			this.mailAttachments.push({
				filename: attachment.relativePath,
				reference: '',
				progress: 0,
				size: 0,
				valid: true,
			})
		);
		for (const droppedFile of attachments) {
			if (droppedFile.fileEntry.isFile) {
				const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
				fileEntry.file((file: File) => this.processFile(file));
			}
		}
	}

	private processFile(file: File) {
		const existing = this.mailAttachments.find((a) => a.filename === file.name);
		if (!existing) {
			this.mailAttachments.push({ filename: file.name, reference: '', progress: 0, size: 0, valid: true });
		}
		const index = this.mailAttachments.findIndex((a) => a.filename === file.name);
		if (index === -1) return;
		this.mailAttachments[index].size = Math.round(file.size / 1024);
		const validation = this.validateFile(file);
		if (validation.ok) {
			const uploadFile = this.fileService.uploadFile(file);
			uploadFile.percentageChanges.subscribe((uploadProgressValue) => {
				this.mailAttachments[index].progress = Math.round(uploadProgressValue.progress);
			});
			uploadFile.uploadTask.then(
				(res) => {
					this.mailAttachments[index].reference = res.metadata?.fullPath;
				},
				(error) => this.snackBar.openSnackBarError(error.message)
			);
		} else {
			// not a valid file: keep the row visible (with valid: false) so the user
			// sees the inline message; also surface a snackbar with the reason.
			this.mailAttachments[index].valid = false;
			const messageKey =
				validation.reason === 'size'
					? 'VALIDATIONS.invalid_file_too_large_max_10MB'
					: 'VALIDATIONS.invalid_file_only_pdf_jpg_png_max_10MB';
			this.snackBar.openSnackBarError(this.translate.instant(messageKey, { filename: file.name }));
		}
	}

	removeInvalidAttachments() {
		// Remove orphaned placeholders (never received a fileEntry callback): no filename, no
		// reference, no size and still flagged valid. Files explicitly flagged invalid are kept
		// so the user sees the inline error message and can dismiss them with the clear button.
		this.mailAttachments = this.mailAttachments.filter(
			(attachment) => attachment.valid === false || (attachment.filename && attachment.reference)
		);
	}

	removeAttachment(attachment: any) {
		const index = this.mailAttachments.findIndex((mailAttachment) => mailAttachment === attachment);
		if (this.mailAttachments[index].reference != '' && !this.mailAttachments[index].reference.startsWith('template-attachments/')) {
			this.fileService.deleteFile(this.mailAttachments[index].reference);
		}
		this.mailAttachments.splice(index, 1);
	}

	fileUploadFinished(): boolean {
		return this.mailAttachments.every((attachment) => attachment.progress === 100 && !!attachment.reference);
	}

	validateFile(file: File): { ok: boolean; reason?: 'type' | 'size' } {
		const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
		const typeOk = ALLOWED_MIME_TYPES.includes(file.type) || ALLOWED_EXTENSIONS.includes(extension);
		if (!typeOk) return { ok: false, reason: 'type' };
		if (file.size >= this.maxFileSize) return { ok: false, reason: 'size' };
		return { ok: true };
	}

	validFileSize() {
		let size = 0;
		this.mailAttachments.forEach((attachment) => (size += attachment.size));
		return size * 1024 < this.maxAttachmentSize;
	}
}
